import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MapPin, Package, Phone, Trash2, Copy, SlidersHorizontal } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import AnimatedPrice from "@/components/AnimatedPrice";
import TradePostSheet from "@/components/TradePostSheet";
import { timeAgo } from "@/lib/utils";

const TYPES = [{ id: "all", label: "All" }, { id: "buy", label: "Buyers" }, { id: "sell", label: "Sellers" }];
const SORTS = [{ id: "recent", label: "Newest" }, { id: "price_low", label: "Price ↑" }, { id: "price_high", label: "Price ↓" }];

export default function TradeBoard({ symbol, name }) {
  const qc = useQueryClient();
  const { currency, convert } = useCurrency();
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("recent");
  const [range, setRange] = useState({ min: "", max: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [open, setOpen] = useState(false);
  const toUsd = (v) => (v === "" ? undefined : convert(Number(v), currency, "USD"));
  const key = ["trade", symbol, type, sort, range.min, range.max, currency];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/commodities/${symbol}/trade-posts`, { params: {
      type: type === "all" ? undefined : type, sort, min_price: toUsd(range.min), max_price: toUsd(range.max) } })).data.items,
    refetchInterval: 15000,
  });
  const items = data || [];

  return (
    <div data-testid="trade-board">
      <div className="mb-3 flex items-center gap-2">
        <div className="no-scrollbar flex flex-1 gap-2 overflow-x-auto">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-active={type === t.id} data-testid={`trade-filter-${t.id}`}
                    className="chip pressable shrink-0 px-4 py-2 text-sm font-semibold">{t.label}</button>
          ))}
        </div>
        <button onClick={() => setShowFilters((s) => !s)} data-testid="trade-filter-toggle"
                className="pressable glass flex h-10 w-10 items-center justify-center rounded-full"><SlidersHorizontal size={16} /></button>
      </div>
      <AnimatePresence>
        {showFilters && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="goe-card mb-3 space-y-3 p-3">
              <div className="flex gap-2">
                {["min", "max"].map((k) => (
                  <input key={k} type="number" min="0" value={range[k]} onChange={(e) => setRange((r) => ({ ...r, [k]: e.target.value }))}
                         placeholder={`${k === "min" ? "Min" : "Max"} price (${currency})`} data-testid={`trade-${k}-price`}
                         className="glass w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
                ))}
              </div>
              <div className="flex gap-2">
                {SORTS.map((s) => (
                  <button key={s.id} onClick={() => setSort(s.id)} data-active={sort === s.id} data-testid={`trade-sort-${s.id}`}
                          className="chip pressable flex-1 py-2 text-xs font-bold">{s.label}</button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button onClick={() => setOpen(true)} data-testid="trade-new-post"
              className="btn-accent mb-4 flex w-full items-center justify-center gap-2 py-3 font-extrabold">
        <Plus size={18} /> Post a buy or sell request
      </button>

      {isLoading ? [0, 1].map((i) => <div key={i} className="skeleton mb-3 h-28 w-full" />) : items.length === 0 ? (
        <div className="goe-card px-6 py-10 text-center text-sm text-dim" data-testid="trade-empty">
          No {type === "buy" ? "buyer" : type === "sell" ? "seller" : ""} posts for {name} yet. Be the first to post.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p, i) => <TradeCard key={p.id} p={p} i={i} onDeleted={() => qc.invalidateQueries({ queryKey: ["trade", symbol] })} />)}
        </div>
      )}
      <TradePostSheet open={open} symbol={symbol} name={name} onClose={() => setOpen(false)}
                      onPosted={() => qc.invalidateQueries({ queryKey: ["trade", symbol] })} />
    </div>
  );
}

function TradeCard({ p, i, onDeleted }) {
  const { user } = useAuth();
  const buy = p.type === "buy";
  const del = async () => {
    try { await api.delete(`/trade-posts/${p.id}`); toast.success("Post removed"); onDeleted(); }
    catch { toast.error("Could not delete"); }
  };
  const copy = () => { navigator.clipboard?.writeText(p.contact); toast.success("Contact copied"); };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }}
                className="goe-card p-4" data-testid={`trade-post-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="rounded-full px-2.5 py-1 text-[10px] font-extrabold tracking-wider"
                style={{ background: buy ? "rgba(56,189,248,0.15)" : "rgba(52,208,127,0.15)", color: buy ? "#38BDF8" : "var(--green)" }}>
            {buy ? "BUYER · WANTED" : "SELLER · AVAILABLE"}
          </span>
          <p className="mt-2 flex items-center gap-1.5 font-display font-bold"><Package size={15} className="accent" />{p.quantity}</p>
        </div>
        <div className="text-right">
          <AnimatedPrice amount={p.price} from={p.currency} className="font-display block text-lg font-extrabold accent" />
          <p className="text-[11px] text-dim">{buy ? "willing to pay" : "asking"}</p>
        </div>
      </div>
      {p.note && <p className="mt-2 text-sm" style={{ color: "var(--text)" }}>{p.note}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-dim">
        {p.location && <span className="flex items-center gap-1"><MapPin size={12} />{p.location}</span>}
        <button onClick={copy} data-testid={`trade-contact-${p.id}`} className="pressable flex items-center gap-1 font-semibold accent">
          <Phone size={12} />{p.contact}<Copy size={11} />
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between border-t pt-2 text-xs text-dim" style={{ borderColor: "var(--border)" }}>
        <span>{p.author?.name} · {timeAgo(p.created_at)}</span>
        {p.author_id === user.id && (
          <button onClick={del} data-testid={`trade-delete-${p.id}`} className="pressable flex items-center gap-1 font-bold" style={{ color: "var(--red)" }}>
            <Trash2 size={13} /> Delete
          </button>
        )}
      </div>
    </motion.div>
  );
}
